/**
 * Badge Component
 *
 * A versatile badge component for status indicators, verification badges,
 * and labels. Supports multiple variants and optional icons.
 */

import type { ReactNode } from 'react';

export interface BadgeProps {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'premium' | 'verified' | 'gold' | 'platinum';
  size?: 'sm' | 'md' | 'lg';
  icon?: string;
  iconPosition?: 'left' | 'right';
  rounded?: 'md' | 'lg' | 'full';
  className?: string;
  pulse?: boolean;
}

export default function Badge({
  children,
  variant = 'default',
  size = 'md',
  icon,
  iconPosition = 'left',
  rounded = 'full',
  className = '',
  pulse = false,
}: BadgeProps) {
  const baseClasses = 'inline-flex items-center font-medium transition-all';

  const variantClasses: Record<Required<BadgeProps>['variant'], string> = {
    default: 'bg-gray-100 text-gray-700 dark:bg-[var(--pv-color-surface-soft)] dark:text-[var(--pv-color-text-muted)]',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-[#FEF8E8] text-[#8C6A0B] ring-1 ring-inset ring-[#FBE5A3] dark:bg-[#FBD35A]/15 dark:text-[#FBD35A] dark:ring-[#FBD35A]/30',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    info: 'bg-[#E2F6FD] text-[#00759F] dark:bg-[#00AEEF]/15 dark:text-[#5FCBF5]',
    primary: 'bg-[var(--pv-color-primary)]/10 text-[var(--pv-color-primary)] dark:bg-[var(--pv-color-primary)]/20',
    // THEME.md §2.4c G4 — the top tier is green *edged in gold*, which is the
    // board's pairing. Gold reads 4.57:1 on #004D40, so here it can be the
    // full-strength colour.
    premium: 'bg-[#004D40] text-white shadow-brand-xs ring-1 ring-inset ring-[#FFC107]/70 [&>i]:text-[#FBD35A]',
    verified: 'bg-[var(--pv-color-primary)] text-white shadow-brand-xs',
    // §2.4c G3. Was `bg-amber-500 text-white` — white on amber-500 is 2.3:1 and
    // failed AA outright. A soft chip with gold-TEXT is both on-brand and legible.
    gold: 'bg-[#FEF8E8] text-[#8C6A0B] ring-1 ring-inset ring-[#FBE5A3] [&>i]:text-[#B58A0A] dark:bg-[#FBD35A]/15 dark:text-[#FBD35A] dark:ring-[#FBD35A]/30 dark:[&>i]:text-[#FBD35A]',
    platinum: 'bg-[#566B66] text-white shadow-brand-xs',
  };

  const sizeClasses: Record<Required<BadgeProps>['size'], string> = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-xs gap-1.5',
    lg: 'px-3 py-1.5 text-sm gap-2',
  };

  const roundedClasses: Record<Required<BadgeProps>['rounded'], string> = {
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  const iconSizeClasses: Record<Required<BadgeProps>['size'], string> = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const badgeClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${roundedClasses[rounded]} ${className}`;

  return (
    <span className={`${badgeClasses} ${pulse ? 'animate-pulse' : ''}`}>
      {icon && iconPosition === 'left' && (
        <i className={`${icon} ${iconSizeClasses[size]}`}></i>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <i className={`${icon} ${iconSizeClasses[size]}`}></i>
      )}
    </span>
  );
}

// Pre-configured badge variants for common use cases
export function VerifiedBadge({ className = '' }: { className?: string }) {
  return (
    <Badge variant="verified" icon="ri-verified-badge-fill" size="sm" className={className}>
      موثوق
    </Badge>
  );
}

export function GoldBadge({ className = '' }: { className?: string }) {
  return (
    <Badge variant="gold" icon="ri-vip-crown-fill" size="sm" className={className}>
      ذهبي
    </Badge>
  );
}

export function PlatinumBadge({ className = '' }: { className?: string }) {
  return (
    <Badge variant="platinum" icon="ri-vip-diamond-fill" size="sm" className={className}>
      بلاتيني
    </Badge>
  );
}

export function FeaturedBadge({ className = '' }: { className?: string }) {
  return (
    <Badge variant="premium" icon="ri-star-fill" size="sm" className={className}>
      مميز
    </Badge>
  );
}

export function LicensedBadge({ className = '' }: { className?: string }) {
  return (
    <Badge variant="success" icon="ri-shield-check-fill" size="sm" className={className}>
      مرخص
    </Badge>
  );
}
